import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Download, Trash2, ShieldAlert, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DataPrivacySection() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const token = localStorage.getItem('token');

  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/account/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      // Trigger download
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `grassroots-coaching-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Data export downloaded');
    } catch (e) {
      toast.error('Export failed. Please try again or contact support.');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (confirm !== 'DELETE') {
      toast.error('Type DELETE in capitals to confirm');
      return;
    }
    if (!password) {
      toast.error('Password is required');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/account`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { password, confirm },
      });
      toast.success('Your account has been permanently deleted');
      setDeleteOpen(false);
      // Tear down session + redirect to landing
      setTimeout(() => {
        logout();
        navigate('/');
      }, 800);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Account deletion failed';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card data-testid="data-privacy-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
            Data & Privacy
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Exercise your rights under GDPR, the Irish Data Protection Act, CCPA, and other applicable laws.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Export */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border border-gray-200 bg-gray-50">
            <div>
              <p className="font-medium text-gray-900">Download your data</p>
              <p className="text-sm text-gray-600 mt-0.5">
                Get a JSON file with every record we hold about your account — profile, teams, players, matches, training, finances, journal, welfare and more.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-data-btn"
              className="shrink-0"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
              )}
              {exporting ? 'Preparing…' : 'Download JSON'}
            </Button>
          </div>

          {/* Delete */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border border-red-200 bg-red-50">
            <div>
              <p className="font-medium text-red-900">Delete account</p>
              <p className="text-sm text-red-800/80 mt-0.5">
                Permanently erase your profile and all associated data. This action is irreversible. Any active subscription will be cancelled.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
              data-testid="delete-account-btn"
              className="shrink-0"
            >
              <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Delete my account
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={deleteOpen} onOpenChange={(open) => !deleting && setDeleteOpen(open)}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ShieldAlert className="w-5 h-5" />
              Delete account permanently?
            </DialogTitle>
            <DialogDescription className="pt-2">
              This will erase your profile, all players, matches, training sessions, finances, journal entries, welfare logs, tactics boards, and chat history. Active Stripe subscriptions will be cancelled.
              <br /><br />
              <strong>This cannot be undone.</strong> Consider downloading your data first.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="delete-password">Confirm your password</Label>
              <Input
                id="delete-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your current password"
                data-testid="delete-password-input"
                disabled={deleting}
              />
            </div>
            <div>
              <Label htmlFor="delete-confirm">
                Type <code className="px-1 bg-gray-100 rounded">DELETE</code> to confirm
              </Label>
              <Input
                id="delete-confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="DELETE"
                data-testid="delete-confirm-input"
                disabled={deleting}
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
              data-testid="delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting || confirm !== 'DELETE' || !password}
              data-testid="delete-confirm-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Permanently delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
